import { SafeHtml } from '@angular/platform-browser';
import { PostType } from './post-type.enum';
import { PostStatus } from './post-status.enum';
import { PostReadStatus } from './post-read-status.enum';
import { RSVPValue } from './rsvp-value.enum';
import { PostFormAllowSignature } from './post-form-allow-signature.enum';
import { PostFormAllowAttachments } from './post-form-allow-attachments.enum';
import { FormlyFieldType } from './formly-field-type.enum';
import { FormGroup } from '@angular/forms';
import { GroupDto } from '../../groups/models/GroupDto';
import { ChatDto } from '../../messages/models/ChatDto';
import { School } from '../../../models/school.model';
import { DeletableBaseModel } from '../../../models/core-base.model';
import { User } from '../../../models/user.model';
import { PostFormSubmissionApproval } from './post-form-submission-approval.enum';
import { Student } from '../../student/models/Student';
import { PostTag } from './post-tag.model';
import { PostOnBehalfOf } from './post-on-behalf-of.model';
import { PaginationModel } from '../../shared/pagination-model';
import { ConferenceSlotType } from './conference-slot-type.enum';

export class Post extends DeletableBaseModel {
  Title: string;
  Description: string;
  SmsDescription?: string | null;
  PostTypeID: PostType;
  PostedDateUTC: Date | null | undefined;
  ScheduledDateUTC: Date | null | undefined;
  /** Schedule date ('yyyy-MM-dd', local) picked on a draft but not yet confirmed; only the post editor uses it. */
  DraftScheduledDate?: string | null;
  /** Schedule time ('HH:mm:ss', local) picked on a draft but not yet confirmed; only the post editor uses it. */
  DraftScheduledTime?: string | null;
  ExpiredDateUTC: Date | null | undefined;
  PostTagId?: number | null;
  PostTag?: PostTag | null;
  PostOnBehalfOfId?: number | null;
  PostOnBehalfOf?: PostOnBehalfOf | null;
  PostCreatorGetsCommunications: boolean;
  /** When true, recipients cannot reply: Reply button and replies list are hidden, and the backend rejects sends. */
  DisallowReplies: boolean = false;
  /** When true, anyone can open the published post from its share link (/p/:id) without logging in. */
  IsPublic: boolean = false;
  PostDesignId?: number | null;

  /** GrapesJS projectData — editor state, not viewer content. Only populated when the caller asks
   *  for it (getPostByIdAsync's `includeDesign`), i.e. the edit and designer screens. What the
   *  detail view renders is Description, the designer's HTML output. */
  DesignJson?: string | null;

  /** True when the post has designer content. Lets either shape — feed card or detail load — pick
   *  the designed render branch (which renders Description) without shipping the DesignJson blob. */
  IsDesigned?: boolean;

  PostStatus: PostStatus;
  PostReadStatus: PostReadStatus;

  /** Set by the year-end purge job; staff see these in the Archived bucket with an "Auto Archived" badge */
  AdminArchived: boolean = false;

  RecipientGroups: PostRecipientGroup[] = [];
  Responders: PostResponder[] = [];
  RelatedChats: ChatDto[] = [];
  PostSchools: PostSchool[] = [];

  PostFiles: PostFile[] = [];
  PostForms: PostForm[] = [];
  PostVolunteerLists: PostVolunteerList[] = [];
  PostConferenceLists: PostConferenceList[] = [];
  PostSignUpLists: PostSignUpList[] = [];
  PostEvent?: PostEvent;
  PaymentRequests: PostPaymentRequest[] = [];

  ViewCount: number | null = null;
  TranslatedLanguageCount: number;

  LikeCount: number = 0;
  HasLiked: boolean = false;

  /** Current version number of the post (starts at 1, increments on each resubmit) */
  CurrentVersion: number = 1;

  /** Optional message explaining what changed in the most recent edit */
  LastEditMessage?: string | null;

  // --- Feed projection (GetPosts) only ---
  // The feed ships a lightweight card: no deep add-on trees. Those are fetched on demand when a
  // card is expanded (getPostByIdAsync). These fields back the collapsed card so it can render
  // without them.

  /** Per-add-on existence/counts for the collapsed footer chips. Undefined on the detail route,
   *  where the full add-on collections are loaded instead. */
  HasEvent?: boolean;
  FormCount?: number;
  VolunteerCount?: number;
  ConferenceCount?: number;
  SignUpCount?: number;
  PaymentCount?: number;
  FileCount?: number;

  /** True when the requesting user already has a template saved from this post. Lets the collapsed
   *  card label its button "Edit Template" without a per-card lookup. Undefined on the detail
   *  route, which resolves the template itself. */
  HasTemplate?: boolean;

  /** The phone-call message dialed out with the post (typed text, or a recording), so the card
   *  and detail view can show what the call said. Any post type except a Quick Notice can carry
   *  one; null/undefined when the post has no call. */
  PhoneCallMessage?: CriticalAlertPhoneCall | null;
}

/** Critical Alert phone-call message, as returned on the post (see CriticalAlertPhoneCallDto). */
export interface CriticalAlertPhoneCall {
  /** 0 = recording, 1 = typed text read by text-to-speech. */
  MessageType: number;
  TTSText: string | null;
  /** Public GUID of the recording; play it via `/api/PhoneCall/audio/{AudioGuid}`. */
  AudioGuid: string | null;
  HasRecording: boolean;
  HasText: boolean;
}

/** Represents a historical version snapshot of a post */
export interface PostVersion {
  Id: number;
  PostId: number;
  VersionNumber: number;
  SnapshotJson: string;
  EditMessage?: string | null;
  CreatedDateUTC: Date;
  CreatedBy: number;
}

export class PostTranslationUserDTO {
  UserId: number;
  FirstLastName: string | null;
}

export class PostTranslationSummaryDTO {
  LanguageId: number;
  LanguageName: string | null;
  TranslatedTitle: string | null;
  TranslatedDescription: string | null;
  TranslationDate: string | null;
  IsOriginalSource: boolean;
  Users: PostTranslationUserDTO[];
}

export class PostResponder extends DeletableBaseModel {
  PostId: number;
  UserId: number;
  User: User | null;
}

export class PostFile {
  Id: number;
  PostId: number;
  Title: string;
  FileName: string;
  Extension: string;
  FileBytes?: string;
  ContentType: string;
  BlobUrl?: string;
  StorageType?: number; // 0 = Database, 1 = AzureBlob
  SecureUrl?: string;

  // Helpers
  FileString?: string;
}

export interface InlineImageListItem {
  PostFileId: number;
  Url: string;
  FileName: string;
  CreatedDateUTC: string | null;
  UploadedByUserId: number | null;
}

export interface PostDesign {
  Id: number;
  DesignName: string;
  DesignDescription: string | null;
  DesignJson: string;
  SchoolId: number | null;
  School: { Id: number; SchoolName: string } | null;
  ThumbnailUrl: string | null;
}

export interface PostDesignThumbnail {
  FileBytes: string; // base64
  ContentType: string;
  FileName: string;
}

export class PostRecipientGroup extends DeletableBaseModel {
  PostId: number;
  RecipientGroupId: number;
  Group?: GroupDto;
}

export class PostSchool extends DeletableBaseModel {
  PostId: number;
  SchoolId: number;
  School?: School;
}

export class PostEvent extends DeletableBaseModel {
  PostId: number = -1;
  Post?: Post | null;
  Title?: string;
  Date: Date;
  StartTime?: string;
  EndTime?: string;
  RecurrenceRule?: string;
  EnableRSVP: boolean = false;
  RSVPLimit?: number;
  RSVPAllowChildren: boolean = false;
  ReminderHours?: number;
  IsCancelled: boolean = false;
  CancellationDateUTC?: string;
  CancelledBy?: number;
  CancelledByUser?: User;
  RSVPRequireHeadcount: boolean = false;
  Timezone: string | undefined;

  RSVPs: PostEventRSVP[];

  CurrentRSVPNumber: number;

  // TODO not the right way to do this, update in calendar component later
  isNhaEvent?: boolean; // Flag to identify NHA Calendar events vs Post events
  IsConferenceEvent?: boolean; // Flag to identify conference events
  ConferenceSlotName?: string; // Conference entries only: the booked slot's name
  ConferenceStudentName?: string; // Conference entries only: the student it was booked for
  ConferenceNotes?: string; // Conference entries only: booking notes
  PostCreatorName?: string; // Sign-up/volunteer/conference entries: who created the post
  BookedByName?: string; // Volunteer/conference entries: household member it belongs to (never the viewer)
  SanitizedDescription?: SafeHtml | null; // Frontend-only: description sanitized once at load (NHA events)
  IsSignUpEvent?: boolean; // Flag to identify sign-up day/item events
  // Sign-up entries only: the items signed up for that day (one card per list per day).
  // SignedUpBy is set only when a household member other than the viewer signed up.
  SignUpItems?: { ItemName: string; Quantity?: number; Notes?: string; SignedUpBy?: string }[];
  IsVolunteerEvent?: boolean; // Flag to identify volunteer day/task events
  VolunteerTaskName?: string; // Volunteer entries only: the task the user volunteered for
  VolunteerNotes?: string; // Volunteer entries only: notes on the volunteer signup
  IsAllDayEvent?: boolean;
  StartDateTime?: string; // UTC start instant for timed NHA events (Date alone is midnight-anchored)

  constructor(postId: number) {
    super();
    this.PostId = postId;
  }
}

export class PostEventRSVP extends DeletableBaseModel {
  PostEventId: number;
  UserId: number;
  OccurrenceDate?: string | null;
  User?: User | null;
  NumberChildren?: number;
  NumberAdults?: number;
  Notes: string;
  RSVPValue: RSVPValue;

  constructor() {
    super();
    this.NumberAdults = 0;
    this.NumberChildren = 0;
    this.Notes = '';
    this.RSVPValue = RSVPValue.Unset;
  }
}

/** Aggregate attendance for one occurrence (OccurrenceDate null = series-level). */
export interface EventOccurrenceCapacity {
  OccurrenceDate?: string | null;
  /** Total people attending (adults + children, a headcount-less "Yes" = 1). */
  Attending: number;
}

export class PostForm extends DeletableBaseModel {
  PostId: number;
  Post?: Post | null;
  Title?: string;
  FormCloseDate: Date | undefined;
  AllowSignature: PostFormAllowSignature = PostFormAllowSignature.Disallowed;
  AllowAttachments: PostFormAllowAttachments = PostFormAllowAttachments.Disallowed;
  AllowStudentInformation: boolean = true;
  AllowSubmissionsAfterDueDate: boolean = false;
  FormData: FormlyFieldConfigEditing[] = [new FormlyFieldConfigEditing()];
  QuestionIdentity: number = 1;
  Submissions: PostFormSubmission[] = [];

  // Helpers
  Fields: PostFormFieldDTO[];
  AvailableStudents: Student[] = [];

  // TODO how are these used?
  model: any;
  formGroup: FormGroup;

  constructor(postId: number) {
    super();
    this.PostId = postId;
  }
}

export class PostFormSubmission extends DeletableBaseModel {
  FormId: number;
  UserId?: number;
  User?: User | null;
  StudentId?: number;
  Student?: Student | null;
  Signature?: string;
  ApprovalType: PostFormSubmissionApproval;
  StudentNameFreeText?: string;
  Attachments?: PostFormAttachment[];
  Responses?: PostFormResponse[] = [];

  // Helper for easier access to responses
  MappedResponses?: { [key: number]: string } = {};

  // Form submission response data as key-value pairs
  Data: object;
}

export class PostFormAttachment extends DeletableBaseModel {
  SubmissionId: number;
  Title: string;
  FileName: string;
  Extension: string;
  FileBytes?: string;
  ContentType: string;
  BlobUrl?: string;
  StorageType?: number;  // 0 = Database, 1 = AzureBlob
  SecureUrl?: string;
}

export class PostFormResponse extends DeletableBaseModel {
  SubmissionId: number;
  FieldId: number;
  Response: string;
}

export class PostFormFieldDTO {
  Label: string;
  Key: number;
  Required: boolean;
  Type: 'input' | 'textarea' | 'select' | 'multicheckbox' | 'datepicker';
  Options: string[];
}

export class PostVolunteerList extends DeletableBaseModel {
  PostId: number;
  Title?: string;
  Days: PostVolunteerListDay[] = [new PostVolunteerListDay()];

  constructor(postId: number) {
    super();
    this.PostId = postId;
  }
}

export class PostVolunteerListDay extends DeletableBaseModel {
  VolunteerListId: number;
  Date: Date | undefined;
  Tasks: PostVolunteerListTask[] = [new PostVolunteerListTask()];
}

export class PostVolunteerListTask extends DeletableBaseModel {
  VolunteerListDayId: number;
  TaskName: string = "";
  NumberNeeded: number = 1;
  StartTime?: string | undefined = undefined;
  EndTime?: string | undefined = undefined;
  Users: PostVolunteerListUser[] = [];
}

export class PostVolunteerListUser extends DeletableBaseModel {
  VolunteerListTaskId: number;
  Notes?: string = "";
  UserName?: string = "";
  IsSelf: boolean = false;
  UserId?: number;
  User?: User | null;
  /** Contact info entered when signing up someone else; self sign-ups read from CreatedByUser. */
  Email?: string | null;
  Phone?: string | null;
}

export class PostConferenceList extends DeletableBaseModel {
  PostId: number;
  Title?: string;
  Days: PostConferenceListDay[] = [new PostConferenceListDay()];

  constructor(postId: number) {
    super();
    this.PostId = postId;
  }
}



export class PostConferenceListDay extends DeletableBaseModel {
  ConferenceListId: number;
  Date: Date | undefined;
  Slots: PostConferenceSlot[] = [new PostConferenceSlot()];
}

export class PostConferenceSlot extends DeletableBaseModel {
  ConferenceListDayId: number;
  SlotName?: string = "";
  StartTime: string = "";
  EndTime: string = "";
  SlotType: ConferenceSlotType = ConferenceSlotType.InPerson;
  Users: PostConferenceSlotUser[] = [];
  AutoFillGroupId?: number; // For grouping autofill-generated slots
}

export class PostConferenceSlotUser extends DeletableBaseModel {
  ConferenceSlotId: number;
  Notes?: string = "";
  UserName?: string = "";
  IsSelf: boolean = false;
  UserId?: number;
  User?: User | null;
  StudentId?: number;
  Student?: Student | null;
  PreferredSlotType?: ConferenceSlotType;
  AttendanceStatus: AttendanceStatus = AttendanceStatus.NotMarked;
  AttendanceMethod: AttendanceMethod = AttendanceMethod.NotSpecified;
  AttendanceNotes?: string;
  AttendanceMarkedDateUTC?: Date;
  AttendanceMarkedBy?: number;
  AttendanceMarkedByUser?: User;
}

export class PostSignUpList extends DeletableBaseModel {
  PostId: number;
  Title?: string;
  Days: PostSignUpListDay[] = [new PostSignUpListDay()];

  constructor(postId: number) {
    super();
    this.PostId = postId;
  }
}

export class PostSignUpListDay extends DeletableBaseModel {
  SignUpListId: number;
  Date: Date | undefined;
  Items: PostSignUpListItem[] = [new PostSignUpListItem()];
}

export class PostSignUpListItem extends DeletableBaseModel {
  SignUpListDayId: number;
  ItemName: string = "";
  NumberNeeded: number = 1;
  Users: PostSignUpListUser[] = [];
}

export class PostSignUpListUser extends DeletableBaseModel {
  UserName?: string = "";
  SignUpListItemId: number;
  IsSelf: boolean = false;
  Notes?: string = "";
  Quantity: number = 1;
  /** Contact info entered when signing up someone else; self sign-ups read from CreatedByUser. */
  Email?: string | null;
  Phone?: string | null;
}

export class PostPaymentRequest extends DeletableBaseModel {
  PostId: number;
  Post?: Post | null;
  Title?: string;
  PaymentDueDate?: Date;
  AllowInPersonPayment: boolean;
  SchoolId?: number | null;
  School?: School | null;
  RequestOptions: PostPaymentRequestOption[] = [new PostPaymentRequestOption(this.Id)];
  Payments: PostPayment[] = [];

  constructor(postId: number) {
    super();
    this.PostId = postId;
  }
}

export class PostPaymentRequestOption extends DeletableBaseModel {
  PaymentRequestId: number;
  Price: number;
  OptionName: string = '';
  TotalAvailable?: number | null;
  RemainingAvailable?: number | null;
  Image?: PaymentRequestOptionImage;
  Variants: PaymentRequestOptionVariant[] = [];

  constructor(paymentRequestId: number) {
    super();
    this.PaymentRequestId = paymentRequestId;
  }
}

export enum PaymentVariantType {
  Selection = 0,
  FreeText = 1
}

export class PaymentRequestOptionImage extends DeletableBaseModel {
  PaymentRequestOptionId: number;
  FileName: string = '';
  ContentType: string = '';
  BlobUrl?: string;
  StorageType?: number; // 0 = Database, 1 = AzureBlob
  FileBytes?: string;
  SecureUrl?: string;
  FileString?: string; // For base64 upload from frontend
}

export class PaymentRequestOptionVariant extends DeletableBaseModel {
  PaymentRequestOptionId: number;
  VariantName: string = '';
  VariantType: PaymentVariantType = PaymentVariantType.Selection;
  IsRequired: boolean = true;
  SortOrder: number = 0;
  Choices: PaymentRequestOptionVariantChoice[] = [];

  constructor(optionId: number = 0) {
    super();
    this.PaymentRequestOptionId = optionId;
  }
}

export class PaymentRequestOptionVariantChoice extends DeletableBaseModel {
  PaymentRequestOptionVariantId: number;
  ChoiceValue: string = '';
  SortOrder: number = 0;

  constructor(variantId: number = 0) {
    super();
    this.PaymentRequestOptionVariantId = variantId;
  }
}

export class PaymentOptionVariantSelection extends DeletableBaseModel {
  PaymentOptionId: number;
  PaymentRequestOptionVariantId: number;
  SelectedChoiceId?: number;
  FreeTextValue?: string;
}

export class PostPaymentOption extends DeletableBaseModel {
  PaymentId: number;
  Payment: PostPayment | null;
  PaymentRequestOptionId: number;
  Quantity: number;
  OptionName: string;
  VariantSelections: PaymentOptionVariantSelection[] = [];
}

export class PostPayment extends DeletableBaseModel {
  PaymentRequestId: number;
  IsInPersonPayment: boolean;
  Provider?: 'Stripe' | 'CyberSource';
  ExternalTransactionId?: string;
  ExternalPaymentMethod?: string;
    /**Amount is in dollars, not cents*/
  Amount: number;
  Notes?: string;
  IsRefunded: boolean;
  RefundedBy?: number;
  RefundedByUser?: User | null;
  RefundedExternally?: boolean;
  RefundedDateUTC?: Date;
  InPersonName?: string;
  InPersonType?: 'check' | 'cash';
  InPersonDate?: Date;
  InPersonCheckNumber?: number;
  Options: PostPaymentOption[];
  LinkedStudentNames: string[];
  /** "LastName FirstName" of the payer's alphabetically-first linked student — the
   *  Excel export's sort key, so rows come out in student roster order. */
  LinkedStudentSortName?: string;
}

/** Recipients with no (non-refunded) payment anywhere in their household, plus the
 *  household-level count shown on the "Not bought" message option. */
export interface PaymentNonBuyers {
  Users: User[];
  HouseholdCount: number;
}

/** Local-calendar 'yyyy-MM-dd'. Never use toISOString() for this - it shifts the day in UTC-negative zones. */
export function toDateKey(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export class PostFeedCalendarDTO {
  EventDays: string[] = [];
  Events: PostFeedCalendarOccurrence[] = [];
}

export class PostFeedCalendarOccurrence {
  PostEventId: number;
  PostId: number;
  /** 'yyyy-MM-dd'. Kept as a string so no timezone is ever applied to it. */
  OccurrenceDate: string;
  Title?: string;
  EventTitle?: string;
  PostTypeID: PostType;
  StartTime?: string;
  EndTime?: string;
  Timezone?: string;
  IsRecurring: boolean;
  IsCancelled: boolean;
  EnableRSVP: boolean;
  UserRsvpValue?: RSVPValue;
  IsConferenceEvent: boolean;
}

export class PostUpcomingItemsDTO {
  PostFormRollupList: AddonRollupDTO[] = [];
  PostSignUpRollupList: AddonRollupDTO[] = [];
  PostVolunteerRollupList: AddonRollupDTO[] = [];
  PostConferenceRollupList: AddonRollupDTO[] = [];
  PostPaymentRollupList: AddonRollupDTO[] = [];
  PostFiles: PostFile[] = [];
}

export class AddonRollupDTO {
  Id: number = -1;
  PostId: number = -1;
  AddonName: string = '';
  DueDate: Date | undefined;
  SubmissionCount: number = 0;
}

export class PostPaginationDTO {
  PageSize: number;
  CurrentPage: number;
  HasMore: boolean;
  Results: Post[];
  Groups: GroupDto[];
}

export class PaymentLineVariantSelectionDTO {
  PaymentRequestOptionVariantId: number;
  SelectedChoiceId?: number;
  FreeTextValue?: string;
}

export class PaymentLineItemDTO {
  OptionId: number;
  Quantity: number;
  VariantSelections: PaymentLineVariantSelectionDTO[] = [];
}

export class PostPaymentCreateDTO {
  PaymentRequestId: number;
  Notes?: string;
  OptionsAndQuantities: { [key: number]: number };
  /** Per-line detail (incl. variant selections) so the server can persist them onto receipts. */
  LineItems?: PaymentLineItemDTO[];
  IsNativeRequestor?: boolean; // True for web, false for mobile
}

export class PostPaymentCreateInPersonDTO extends PostPaymentCreateDTO {
  InPersonName?: string;
  InPersonType?: 'check' | 'cash';
  InPersonDate?: string;
  InPersonCheckNumber?: number;
}

export class PostPaymentFinalizeInPersonDTO {
  InPersonName?: string;
  InPersonType?: 'check' | 'cash';
  InPersonDate?: string;
  InPersonCheckNumber?: number;
  Notes?: string;
}

export class FormlyFieldConfigEditing {
  key: number = 0;
  type: FormlyFieldType = FormlyFieldType.Input;
  props: FormlyFieldProps = new FormlyFieldProps();
}

export class FormlyFieldProps {
  label: string;
  required: boolean;
  options: FormlyFieldPropOption[];
}

export class FormlyFieldPropOption {
  label: string;
  value: string;

  constructor(label: string, value: string) {
    this.label = label;
    this.value = value;
  }
}

export class PostCommunicationSummary extends PaginationModel<PostCommunicationSummaryEntry> {
}

export class PostLikes extends PaginationModel<User> {
}

// Page-independent counts, fetched once when the communications modal opens.
export class PostCommunicationStats {
  EmailPendingCount: number;
  EmailSentCount: number;
  EmailReadCount: number;
  EmailClickedCount: number;
  TextPendingCount: number;
  TextSentCount: number;
  TextClickedCount: number;
  PushPendingCount: number;
  PushSentCount: number;
  PushClickedCount: number;
  InAppPendingCount: number;
  InAppViewedCount: number;
  HasPhoneCall: boolean;
  PhonePendingCount: number;
  PhoneFailedCount: number;
  PhoneNoAnswerCount: number;
  PhoneVoicemailCount: number;
  PhoneAnsweredCount: number;
  HasEventReminders: boolean;
  HasRemovedRecipients: boolean;
}

export class PostCommunicationSummaryEntry extends DeletableBaseModel {
  UserId: number;
  UserName: string;
  UserLanguage?: string;
  StudentNames: string[];
  EntryType: PostCommunicationEntryType;
  EmailStatus?: PostCommunicationStatus;
  EmailSentDate?: string;
  EmailViewedDate?: string;
  EmailClickedDate?: string;
  TextStatus?: PostCommunicationStatus;
  TextSentDate?: string;
  TextClickedDate?: string;
  PushStatus?: PostCommunicationStatus;
  PushSentDate?: string;
  PushClickedDate?: string;
  InAppStatus?: PostCommunicationStatus;
  InAppViewedDate?: string;
  EmailCount: number;
  TextCount: number;
  PushCount: number;
  PhoneStatus?: PhoneCallSummaryStatus;
  PhoneDate?: string;
  PhoneCount: number;
  OccurrenceDate?: string;
  IsRemovedRecipient: boolean;
}

export enum PostCommunicationStatus {
  Pending = 0,
  Sent = 1,
  Viewed = 2,
  Clicked = 3
}

// Mirrors the server's PhoneCallSummaryStatus (call log status collapsed for the report).
export enum PhoneCallSummaryStatus {
  Pending = 0,
  Failed = 1,
  NoAnswer = 2,
  Voicemail = 3,
  Answered = 4
}

export enum PostCommunicationEntryType {
  Post = 0,
  EventReminder = 1
}

export enum AttendanceStatus {
  NotMarked = 0,
  Present = 1,
  NoShow = 2,
  Other = 3
}

export namespace AttendanceStatus {
  export function values(): AttendanceStatus[] {
    return [
      AttendanceStatus.NotMarked,
      AttendanceStatus.Present,
      AttendanceStatus.NoShow,
      AttendanceStatus.Other
    ];
  }

  export function displayView(value: AttendanceStatus): string {
    switch (value) {
      case AttendanceStatus.NotMarked: return 'Not Marked';
      case AttendanceStatus.Present: return 'Present';
      case AttendanceStatus.NoShow: return 'No-Show';
      case AttendanceStatus.Other: return 'Other';
      default: return '';
    }
  }

  export function getClass(value: AttendanceStatus): string {
    switch (value) {
      case AttendanceStatus.Present: return 'text-success';
      case AttendanceStatus.NoShow: return 'text-alert';
      case AttendanceStatus.Other: return 'text-warning';
      default: return 'text-text-blue';
    }
  }

  export function getIcon(value: AttendanceStatus): string {
    switch (value) {
      case AttendanceStatus.Present: return 'icon-check icon-green';
      case AttendanceStatus.NoShow: return 'icon-x icon-red';
      case AttendanceStatus.Other: return 'icon-question icon-yellow';
      default: return '';
    }
  }
}

export enum AttendanceMethod {
  NotSpecified = 0,
  InPerson = 1,
  Virtual = 2,
  Email = 3,
  Phone = 4
}

export namespace AttendanceMethod {
  export function values(): AttendanceMethod[] {
    return [
      AttendanceMethod.NotSpecified,
      AttendanceMethod.InPerson,
      AttendanceMethod.Virtual,
      AttendanceMethod.Email,
      AttendanceMethod.Phone
    ];
  }

  export function displayView(value: AttendanceMethod): string {
    switch (value) {
      case AttendanceMethod.NotSpecified: return 'Not Specified';
      case AttendanceMethod.InPerson: return 'In Person';
      case AttendanceMethod.Virtual: return 'Virtual';
      case AttendanceMethod.Email: return 'Email';
      case AttendanceMethod.Phone: return 'Phone';
      default: return '';
    }
  }
}
